import React, { useState, useEffect } from 'react';
import privateApi from "../../apis/privateApi";
import Notification from "../../components/Notification/Notification";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import './LGABudgetCreate.css';
import { useNavigate } from 'react-router-dom';

const LGABudgetCreate = () => {

    const navigate = useNavigate();

    const [year, setYear] = useState('');
    const [type, setType] = useState('Original');
    const [remark, setRemark] = useState('');
    const [lgaId, setLgaId] = useState('');
    const [lgas, setLgas] = useState([]);

    const [loading, setLoading] = useState(false);

    const [notification, setNotification] = useState({
        show: false,
        message: '',
        type: 'info'
    });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState('');
    const [pendingSubmitData, setPendingSubmitData] = useState(null);

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const handleNotificationClose = () => {
        setNotification(prev => ({ ...prev, show: false }));
    };

    const generateToken = () => {
        return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    // ---------------------------------------------
    // Fetch LGAs
    // ---------------------------------------------
    useEffect(() => {
        const fetchLgas = async () => {
            try {
                const res = await privateApi.get('/api/lga/budgets/lgas');
                setLgas(res.data.data || []);
            } catch (err) {
                setNotification({
                    show: true,
                    message: "Failed to load LGAs.",
                    type: "error"
                });
            }
        };
        fetchLgas();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();

        const token = generateToken();
        setConfirmToken(token);

        setPendingSubmitData({
            year,
            type,
            remark,
            lgaId
        });

        setConfirmOpen(true);
    };

    const handleConfirm = async (input) => {

        if (input !== confirmToken) {
            setNotification({
                show: true,
                message: 'Incorrect code entered. Action cancelled.',
                type: 'error'
            });
            setConfirmOpen(false);
            return;
        }

        setConfirmOpen(false);
        setLoading(true);

        try {

            const data = {
                year: Number(pendingSubmitData.year),
                type: pendingSubmitData.type,
                remark: pendingSubmitData.remark,
                lgaId: pendingSubmitData.lgaId,
                level: "lga"
            };

            const res = await privateApi.post('/api/lga/budgets', data);

            setNotification({
                show: true,
                message: `LGA budget ${res.data.data.type} ${res.data.data.year} rev ${res.data.data.revisionNumber} created successfully!`,
                type: 'success'
            });

            setYear('');
            setType('Original');
            setRemark('');
            setLgaId('');

        } catch (err) {

            setNotification({
                show: true,
                message: err.response?.data?.message || 'Failed to create LGA budget.',
                type: 'error'
            });

        } finally {

            setLoading(false);
            setPendingSubmitData(null);

        }
    };

    const handleCancel = () => {
        setConfirmOpen(false);
        setPendingSubmitData(null);
    };

    return (

        <div className="lga-budget-create">

            <h2>Create LGA Budget</h2>

            <Notification
                show={notification.show}
                message={notification.message}
                type={notification.type}
                onClose={handleNotificationClose}
            />

            <form onSubmit={handleSubmit}>

                <div className="lga-budget-create-form-group">
                    <label>LGA:</label>
                    <select
                        value={lgaId}
                        onChange={(e) => setLgaId(e.target.value)}
                        required
                    >
                        <option value="">Select LGA</option>
                        {lgas.map(lga => (
                            <option key={lga._id} value={lga._id}>
                                {lga.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="lga-budget-create-form-group">
                    <label>Year:</label>
                    <select
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        required
                    >
                        <option value="">Select Year</option>
                        {yearOptions.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>

                <div className="lga-budget-create-form-group">
                    <label>Type:</label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        <option value="Original">Original</option>
                        <option value="Supplementary">Supplementary</option>
                    </select>
                </div>

                <div className="lga-budget-create-form-group">
                    <label>Remark:</label>
                    <textarea
                        id="lga-budget-create-textarea"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        placeholder="Optional remark..."
                    />
                </div>

                <div className="lga-budget-create-action-buttons">

                    <button
                        type="submit"
                        className="lga-budget-create-btn"
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Create Budget'}
                    </button>

                    <button
                        onClick={() => navigate(-1)}
                        className="lga-budget-create-back-btn"
                        type="button"
                    >
                        Back
                    </button>

                </div>

            </form>

            <ConfirmDialog
                show={confirmOpen}
                token={confirmToken}
                action={`You are about to create a ${type} LGA budget for ${year}`}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />

        </div>
    );
};

export default LGABudgetCreate;